import { useIsVisible } from "@website/hooks/is-visible";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { Button } from "@website/ui/button";
import { CollapsibleText } from "@website/ui/collapsible-text";
import { Loading } from "@website/ui/loading";
import { deepCompare } from "@website/utils/deep-compare";
import { ReserveComment } from "@website/widget/reserve-comment";
import { ReserveCommentsFilter } from "@website/widget/reserve-comment-popup-scroll/reserve-comment-filter";
import { ReserveCommentsFilterEmptyResult } from "@website/widget/reserve-comment-popup-scroll/reserve-comment-filter/reserve-comment-filter-empty-result";
import { ReserveCommentPopupScrollTypes } from "@website/widget/reserve-comment-popup-scroll/reserve-comment-popup-scroll.types";
import { ReserveRate } from "@website/widget/reserve-rate";
import isEmpty from "lodash-es/isEmpty";
import { useRouter } from "next/router";
import { memo, useEffect } from "react";

const MAX_COMMENTS_COUNT = 5;

const Component = ({
  rate,
  comments,
  onLoadMore,
  isLoading,
  className,
  onLoadFilter,
  filters,
  isInsideDetail,
  size = "normal",
  onClick
}: ReserveCommentPopupScrollTypes) => {
  const router = useRouter();
  const { isMobile } = useViewPort();
  const { reference, inView } = useIsVisible();

  useEffect(() => {
    inView && onLoadMore?.();
  }, [inView, onLoadMore]);

  return (
    <div
      className={`${
        size === "small" ? "" : "h-[calc(100%-64px)]"
      } ${className}`}
      id="container"
    >
      <div
        className={`${
          size === "small"
            ? "mx-0 pt-6 md:gap-4 xl:flex-col"
            : "px-6 py-4 md:gap-[40px] xl:mx-auto xl:flex-row"
        } relative flex w-full flex-col items-center gap-4 self-stretch md:items-start md:pb-[40px] md:pt-6 xl:max-w-[1200px]`}
      >
        {rate && (
          <div
            className={`${
              size === "small"
                ? "flex-col md:flex-row"
                : "flex-col xl:sticky xl:top-0 xl:min-w-[312px] xl:max-w-[312px] xl:pt-[40px]"
            } mx-auto flex w-full flex-auto gap-6`}
          >
            <ReserveRate
              {...rate}
              hasBackground={false}
              showFooter={size !== "small"}
              showTitle={false}
              size="medium"
            />
            {/*{size !== "small" && showSubmit && <ReserveCommentPopupSubmit />}*/}
            <div className="bg-dim-background h-px w-full xl:hidden"></div>
            {rate?.aiSummary && size === "small" && (
              <div className="bg-dim-background text-subtitle-2 flex min-w-[305px] flex-col gap-2 rounded-lg px-3 py-4">
                <CollapsibleText
                  ariaLabel="description"
                  className={`${
                    size === "small" ? "max-h-[305px] overflow-y-auto" : ""
                  } text-on-surface-high-emphasis`}
                  collapsedClassName={
                    size === "small" ? "h-[145px]" : "h-[118px]"
                  }
                  content={rate?.aiSummary}
                  disableScrollWhenCollapsed
                  eventKey={USER_TRACKING_EVENTS?.AI_SUMMARY_COMMENTS as const}
                  isHtml
                />
                <div className="bg-transparent-secondary text-caption mt-auto flex flex-row items-center gap-1 rounded-lg p-2">
                  <MaterialIcon
                    className="text-secondary text-subtitle-1"
                    name="edit"
                    variant="outlined"
                  />
                  <span>
                    برگرفته از نظرات مهمانان واقعی که توسط هوش مصنوعی خلاصه شده
                    است.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        {size === "small" && (
          <div className="flex w-full flex-row items-center justify-between">
            <span className="text-subtitle-1">نظرات اخیر کاربران</span>
            {comments?.length >= MAX_COMMENTS_COUNT && (
              <Button
                color="secondary"
                onClick={onClick}
                text="نظرات بیشتر"
                variant="outline"
              />
            )}
          </div>
        )}
        <div className="relative flex w-full flex-col gap-6 md:self-start">
          {!isEmpty(filters) && (
            <div className="z-10 mr-[-4px] w-[103%] bg-white pb-2 pe-3 ps-1 xl:sticky xl:top-0">
              <ReserveCommentsFilter
                filters={filters}
                isInsideDetail={isInsideDetail}
                onLoadFilter={onLoadFilter}
              />
            </div>
          )}
          {!isEmpty(filters) &&
          !isLoading &&
          !comments?.length &&
          Boolean(
            Object.keys(router?.query)?.filter(
              (item) => item?.startsWith("filter_")
            ).length
          ) ? (
            <div className="pt-[60px]">
              <ReserveCommentsFilterEmptyResult onLoadFilter={onLoadFilter} />
            </div>
          ) : (
            comments?.map((comment) => (
              <ReserveComment key={comment?.id} {...comment} />
            ))
          )}
          {isLoading && (
            <div className="mx-auto">
              <Loading color="primary" size={isMobile ? "sm" : "lg"} />
            </div>
          )}
          <div ref={reference} />
        </div>
      </div>
    </div>
  );
};
export const ReserveCommentPopupScroll = memo(Component, deepCompare);
